import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'
import type { DeviceType } from '@renderer/types/utils'

/**
 * Prefocus — drive one autofocus cycle at the START of the capture countdown, so the
 * lens is already converged by the time the shutter actually fires.
 *
 * Why this exists. libgphoto2 does not simply release the shutter: for Canon EOS it
 * half-presses WITH autofocus and then blocks, polling the camera for a focus result,
 * before it will full-press. That poll sits inside the one `captureImageAsFile()` call
 * the booth makes, and it is ceilinged — 2000ms on the WASM backend (libgphoto2 2.5.28,
 * the version compiled into our libapi.wasm) and 500ms on the native daemon (2.5.34;
 * upstream lowered it in 2.5.32). A body that never emits a focus-info event waits out
 * the whole ceiling and fires anyway. All of that is paid AFTER the countdown reaches
 * zero, which is exactly the dead air operators compare us unfavourably on.
 *
 * Prefocusing does not remove that half-press — the capture still confirms focus, so a
 * guest who moved is still focused on — it just means autofocus has nothing left to
 * search for when the blocking press happens, so it returns almost immediately.
 *
 * IMPORTANT — this only pays off on the NATIVE backend. On the WASM backend the ONLY
 * exposed capture is `captureImageAsFile()` = `gp_camera_capture`, which on Canon ALWAYS
 * performs its own autofocus half-press; there is no binding to capture without AF or to
 * download an image we pressed ourselves (the wrapper exposes six methods, and
 * `consumeEvents` returns a boolean, not a file). So a pre-drive there can only run AF
 * twice (drive + the capture's own, measured ~2.5s SLOWER) or, left engaged, make the
 * capture skip AF and shoot at a stale distance (faster but out of focus — the field
 * regression that prompted this gate). Proven on booth t949mi across every focus mode.
 * The real win needs a native capture op that presses without AF and downloads the
 * object itself; until a booth runs the native backend, prefocus stays inert there.
 *
 * Why `autofocusdrive` and not `eosremoterelease`. `autofocusdrive` is a one-shot
 * toggle (Canon `EOS_DoAf`, Nikon `AfDrive`, Fuji) that holds nothing afterwards, so
 * there is no half-press left engaged to release and nothing to unwind if the session
 * is abandoned mid-countdown. Critically it is ALSO spelled and behaves identically on
 * both our backends. `eosremoterelease` is not: the frozen 2.5.28 WASM build names its
 * choices "Press Half"/"Press Full"/"Immediate" where "Press Half" maps to the
 * no-autofocus variant, while 2.5.32+ renamed them to "Press Half AF"/"Press Half MF"
 * and would reject the old strings outright. Anything built on that config has to
 * branch per backend; this does not.
 */

/**
 * Registered in the Studio flag registry (config/featureFlags.ts, surface "app"),
 * default OFF. Unknown keys resolve to `false`, so a booth on an older Studio — or one
 * that is offline before its first flag fetch — behaves exactly as it did before.
 */
export const CAMERA_PREFOCUS_FLAG = 'camera_prefocus'

/** Non-React read: the capture path runs outside the React tree. */
export function isPrefocusEnabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(CAMERA_PREFOCUS_FLAG, false)
}

/**
 * libgphoto2 config that drives one autofocus cycle. A GP_WIDGET_TOGGLE: 1 runs AF,
 * 0 cancels it. Present under this exact name for Canon, Nikon and Fuji in both 2.5.28
 * and 2.5.34, which is what makes this backend-agnostic.
 */
export const PREFOCUS_CONFIG_NAME = 'autofocusdrive'

/**
 * libgphoto2 config that takes a Canon OUT of the autofocus-engaged state that
 * `autofocusdrive=1` puts it in (EOS_DoAf → EOS_AfCancel). This is not optional. From the
 * libgphoto2 maintainer, on exactly this symptom: "autofocusdrive=1 switches the camera to
 * focusing, but there was no code yet to switch out of it … you can use autofocusdrive=0
 * or cancelautofocus=1". While engaged, a later DoAf is a no-op and — worse — the capture's
 * own half-press performs NO autofocus: libgphoto2 waits out its focus-info timeout and
 * fires at whatever distance the lens was left on. Observed in the field as "focuses on
 * photo 1 only, later photos silent and blurry", with the later presses FASTER than a
 * clean capture because no AF ran at all.
 *
 * `cancelautofocus` rather than `autofocusdrive=0`: on Nikon the AFDrive widget ignores
 * its value and DRIVES autofocus (up to a 5s busy-wait), so `=0` there would add a full
 * AF cycle to every press. `cancelautofocus` only exists on Canon; elsewhere the write
 * fails with "not found" once and is remembered.
 */
export const PREFOCUS_RELEASE_CONFIG_NAME = 'cancelautofocus'

/**
 * Floor between two autofocus drives on one camera. A countdown that is restarted (a
 * retake, a guest tapping through, a recovery that re-enters the capture page) must not
 * turn into a burst of AF commands on a USB channel the live preview is also using.
 * Comfortably shorter than the shortest countdown the booth offers, so a real
 * per-photo prefocus is never the thing that gets throttled.
 */
export const PREFOCUS_MIN_INTERVAL_MS = 1500

/**
 * Consecutive overlaps before a camera stops being asked. An overlap is the ONE
 * harmful case: the shutter press arrived while the autofocus drive was still running,
 * so the press queued behind it on the single USB channel and the drive cost the shot
 * time instead of saving it. That is the dark-venue failure. A slow drive that still
 * finishes inside the countdown is fine — it is exactly the body that gains the most.
 * Two rather than one: one overlap is an awkwardly placed guest, two in a row is the room.
 */
export const PREFOCUS_OVERLAP_LIMIT = 2

/**
 * What the operator told us to do about focus. A real photographic trade-off that only
 * the person who set the booth up can decide, which is why this is a setting and the
 * prefocus mechanism itself is not.
 *
 * The mode decides WHEN the lens gets focused. The press itself, on any backend that
 * can (native daemon, WASM v2), never asks the camera to focus — so whatever the last
 * drive left is what the photo gets, and the shutter is never waiting on autofocus.
 * (The v1 WASM build cannot press without autofocus and keeps its ordinary capture.)
 *
 * - `every-photo`  — drive autofocus before every shot. The default; safe for a roaming
 *                    booth where guests stand wherever they like.
 * - `once-per-session` — drive before the FIRST shot of a session; every later shot
 *                    presses without autofocus on that focus. For a booth with a taped
 *                    floor marker, shots 2..N then cost nothing at all.
 * - `fixed`        — never drive. The operator focused by hand (or with the camera's own
 *                    button) and the booth must not touch it; every shot presses
 *                    without autofocus on that focus.
 */
export type FocusMode = 'every-photo' | 'once-per-session' | 'fixed'

/** Same behaviour a booth had before this setting existed. */
export const DEFAULT_FOCUS_MODE: FocusMode = 'every-photo'

export const FOCUS_MODES: readonly FocusMode[] = ['every-photo', 'once-per-session', 'fixed']

/** Persisted settings are operator-editable JSON; anything unrecognised falls back. */
export function normaliseFocusMode(value: unknown): FocusMode {
  return FOCUS_MODES.includes(value as FocusMode) ? (value as FocusMode) : DEFAULT_FOCUS_MODE
}


/**
 * Which vendor's libgphoto2 driver we are talking to. Decides whether a prefocus drive
 * is even a sensible thing to send, from the 2.5.28/2.5.34 source:
 *   canon — `autofocusdrive` is EOS_DoAf, a latch; drive + no-AF press is the whole plan.
 *   nikon — `autofocusdrive` is AfDrive with a SYNCHRONOUS busy-wait of up to 5s, on
 *           the same op queue the live preview uses. And in live view the Nikon capture
 *           does no AF at all (ptp_nikon_capture, not AfCaptureSDRAM), so this is a
 *           different problem with a different fix; gated off until it has one.
 *   sony  — no `autofocusdrive` widget at all; its half-press is `autofocus=1/0`, and
 *           the Sony capture already waits ≤1s for FocusFound. Nothing to gain today.
 *   fuji  — `autofocusdrive` polls the AF status with NO timeout; a body that never
 *           leaves "focusing" hangs the camera queue for the rest of the session.
 *   other/null — unknown; probe once and let the widget answer.
 */
/**
 * "Wait for focus before capture" — hold the shutter until the autofocus drive started at
 * the countdown has actually finished, instead of firing when the countdown ends.
 *
 * The countdown drive is fire-and-forget precisely so the shutter never waits on it: a
 * guest watching "3… 2… 1…" expects the photo THEN, and a booth that pauses afterwards
 * reads as broken. That is the right default and it stays the default.
 *
 * But it is a trade, not a free win. When the drive has not converged by zero, the press
 * goes through on whatever focus the lens currently holds, and the shot can be soft. Some
 * operators — portrait and product setups especially, where a soft frame is a reprint —
 * would rather the booth took the extra moment. This setting is for them, and it is the
 * only situation where the booth is allowed to be late on purpose.
 *
 * OFF is byte-for-byte the previous behaviour: the wait below never runs.
 */
export const WAIT_FOR_FOCUS_DEFAULT = false

/**
 * Hard ceiling on that wait. A body that never reports focus must not hold the shutter
 * for the rest of the session — past this we press anyway, exactly as if the setting were
 * off. Generous next to a converging Canon drive (typically 300-900ms) and still short
 * enough that a guest reads it as a slow camera rather than a dead booth.
 */
export const FOCUS_WAIT_BUDGET_MS = 4000

export type CameraVendor = 'canon' | 'nikon' | 'sony' | 'fuji' | 'other'

/** USB vendor ids, as libgphoto2's own device table spells them. */
const USB_VENDORS: Record<number, CameraVendor> = {
  0x04a9: 'canon',
  0x04b0: 'nikon',
  0x054c: 'sony',
  0x04cb: 'fuji'
}

export function vendorFromUsbVendorId(vendorId: number | undefined | null): CameraVendor | null {
  if (vendorId === undefined || vendorId === null) return null
  return USB_VENDORS[vendorId] ?? 'other'
}

/** From the daemon's connect reply (libgphoto2 model string, e.g. "Canon EOS R100"). */
export function vendorFromModel(model: string | undefined | null): CameraVendor | null {
  if (!model) return null
  const m = model.toLowerCase()
  if (m.startsWith('canon')) return 'canon'
  if (m.startsWith('nikon')) return 'nikon'
  if (m.startsWith('sony') || m.startsWith('ilce') || m.startsWith('zv-') || m.startsWith('dsc-')) return 'sony'
  if (m.startsWith('fuji')) return 'fuji'
  return 'other'
}

export type PrefocusSkipReason =
  | 'flag-off'
  | 'not-shutter-capture'
  | 'wasm-backend'
  | 'nikon-blocking-drive'
  | 'sony-no-drive-widget'
  | 'fuji-unbounded-drive'
  | 'fixed-focus'
  | 'not-a-dslr'
  | 'capture-in-flight'
  | 'recovery-in-flight'
  | 'unsupported'
  | 'overlaps-capture'
  | 'already-focused-this-session'
  | 'throttled'

export interface PrefocusDecisionInput {
  flagEnabled: boolean
  /** Prefocus is meaningless on the liveview-grab path — there is no shutter to beat. */
  useShutterCapture: boolean
  focusMode: FocusMode
  /**
   * True only on the native libgphoto2 backend. WASM cannot capture without the built-in
   * autofocus, so a pre-drive there is at best wasted and at worst corrupts focus — see
   * the module comment. False keeps prefocus inert on every WASM booth.
   */
  backendSupportsPrefocus: boolean
  /** Vendor of the connected body, or null when unknown (then we probe). */
  vendor: CameraVendor | null
  /**
   * Current session's id, and the session this camera last focused for. Compared rather
   * than counted so a session that ends mid-strip (abandoned, recovered, retaken) starts
   * cleanly instead of inheriting a stale "already done" flag.
   */
  sessionId: string | undefined
  lastPrefocusSessionId: string | null
  deviceType: DeviceType | null | undefined
  /** True only while the physical shutter press itself is in flight — not the
   *  post-processing after it, during which the camera is already free. */
  captureInFlight: boolean
  recoveryInFlight: boolean
  /** False once this body has told us it has no `autofocusdrive` widget. */
  widgetSupported: boolean
  /** True once this body's drives have repeatedly overlapped the shutter press. */
  backedOff: boolean
  lastPrefocusAt: number
  now: number
}

export interface PrefocusDecision {
  run: boolean
  reason?: PrefocusSkipReason
}

/**
 * Pure so the guard order is testable without a camera. Order matters: the cheap
 * configuration checks come before the ones that describe a transient device state, so
 * a booth that will never prefocus (webcam, preview capture, flag off) says so for the
 * honest reason rather than reporting whatever it happened to be doing.
 */
export function decidePrefocus(input: PrefocusDecisionInput): PrefocusDecision {
  if (!input.flagEnabled) return { run: false, reason: 'flag-off' }
  if (!input.useShutterCapture) return { run: false, reason: 'not-shutter-capture' }
  // WASM has no autofocus-free capture, so a pre-drive cannot help there and has been
  // observed to hurt. Native only.
  if (!input.backendSupportsPrefocus) return { run: false, reason: 'wasm-backend' }
  // Vendor gates — see CameraVendor. Configuration facts, so they come before anything
  // that describes a transient device state.
  if (input.vendor === 'nikon') return { run: false, reason: 'nikon-blocking-drive' }
  if (input.vendor === 'sony') return { run: false, reason: 'sony-no-drive-widget' }
  if (input.vendor === 'fuji') return { run: false, reason: 'fuji-unbounded-drive' }
  // The operator pre-focused by hand. Driving autofocus would undo exactly the setup
  // they configured the booth around.
  if (input.focusMode === 'fixed') return { run: false, reason: 'fixed-focus' }
  if (input.deviceType !== 'camera') return { run: false, reason: 'not-a-dslr' }
  if (!input.widgetSupported) return { run: false, reason: 'unsupported' }
  if (input.backedOff) return { run: false, reason: 'overlaps-capture' }
  // One focus for the whole strip. An unknown session id means there is no session to
  // be "once" within, so fall through and focus — the throttle still prevents a burst.
  if (
    input.focusMode === 'once-per-session' &&
    input.sessionId !== undefined &&
    input.sessionId === input.lastPrefocusSessionId
  ) {
    return { run: false, reason: 'already-focused-this-session' }
  }
  // USB/PTP is single-access. Competing with an in-flight shot or a reconnect is how
  // this feature would manufacture the very "busy" errors the retry work exists to avoid.
  if (input.captureInFlight) return { run: false, reason: 'capture-in-flight' }
  if (input.recoveryInFlight) return { run: false, reason: 'recovery-in-flight' }
  if (input.now - input.lastPrefocusAt < PREFOCUS_MIN_INTERVAL_MS) {
    return { run: false, reason: 'throttled' }
  }
  return { run: true }
}

/**
 * Bodies that answered "I have no such widget". Keyed by the Camera INSTANCE, so a
 * reconnect (which constructs a fresh Camera) re-probes automatically and swapping a
 * body mid-shift is never stuck with the previous one's answer.
 */
const camerasWithoutPrefocus = new WeakSet<object>()

export function markPrefocusUnsupported(camera: object): void {
  camerasWithoutPrefocus.add(camera)
}

export function isPrefocusKnownUnsupported(camera: object): boolean {
  return camerasWithoutPrefocus.has(camera)
}

/** Bodies with no `cancelautofocus` widget (non-Canon). Same per-instance keying. */
const camerasWithoutRelease = new WeakSet<object>()

export function markPrefocusReleaseUnsupported(camera: object): void {
  camerasWithoutRelease.add(camera)
}

export function isPrefocusReleaseKnownUnsupported(camera: object): boolean {
  return camerasWithoutRelease.has(camera)
}

/**
 * Bodies whose drives keep overlapping the shutter press. Keyed by the Camera INSTANCE
 * like the unsupported set, so a reconnect re-evaluates — the room may have had its
 * lights turned on since.
 */
const camerasBackedOff = new WeakSet<object>()
const consecutiveOverlaps = new WeakMap<object, number>()

export function isPrefocusBackedOff(camera: object): boolean {
  return camerasBackedOff.has(camera)
}

/**
 * Feed back whether the shutter press found a drive still running. Returns true when
 * THIS sample tripped the backoff, so the caller can say so once rather than every shot.
 * A clean press clears the streak: a booth is not condemned by two bad shots early in
 * the evening if its drives are finishing in time afterwards.
 */
export function recordPrefocusOverlap(camera: object, overlapped: boolean): boolean {
  if (!overlapped) {
    consecutiveOverlaps.set(camera, 0)
    return false
  }
  const streak = (consecutiveOverlaps.get(camera) ?? 0) + 1
  consecutiveOverlaps.set(camera, streak)
  if (streak < PREFOCUS_OVERLAP_LIMIT || camerasBackedOff.has(camera)) return false
  camerasBackedOff.add(camera)
  return true
}

/**
 * True when the camera is telling us the widget does not exist, as opposed to being
 * momentarily busy. Only the former is permanent: mis-classifying a transient error
 * here would silently disable prefocus for the rest of the run on a camera that
 * supports it perfectly well, so this matches narrowly and everything else is retried
 * on the next countdown.
 *
 * Matched against the three exact wordings the stack can produce, verified against the
 * strings in our shipped libapi.wasm and the daemon source rather than guessed:
 *   - "Property 'autofocusdrive' not found." — the web-gphoto2 wrapper's config lookup
 *   - "Unsupported operation"                — libgphoto2 GP_ERROR_NOT_SUPPORTED, which
 *     is also what the Canon driver returns when the body has no DoAf operation
 *   - "no such config widget"                — our own daemon's widget lookup
 *
 * Deliberately NOT matched: a bare error code like "-6", or a loose "not supported".
 * Those appear in unrelated transient messages, and a false positive here is a silent
 * permanent loss of the feature on a camera that supports it, while a false negative
 * costs one throttled retry on the next countdown.
 */
export function isUnsupportedConfigError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err)
  return (
    /no such config widget/i.test(message) ||
    /unsupported operation/i.test(message) ||
    /property\s+'[^']*'\s+not found/i.test(message) ||
    // libgphoto2's own answer to a single-config lookup of a widget this body does not
    // have (GP_ERROR_BAD_PARAMETERS) — what a Sony says to `autofocusdrive`, now that the
    // WASM wrapper decodes it instead of surfacing a bare pointer.
    /^bad parameters$/i.test(message.trim()) ||
    // The v2 binding when `eosremoterelease` offers none of the no-AF press choices.
    /no supported choice for a no-af press/i.test(message)
  )
}
